import React, { useState, useRef, useEffect } from 'react';
import { CiMenuBurger } from "react-icons/ci";
import { IoIosNotificationsOutline } from "react-icons/io";
import { IoIosArrowDown } from "react-icons/io";
import { useDispatch, useSelector } from 'react-redux';
import { toggleAuthentication } from '../../redux/adminSlice';
import { logout, toggleRole, selectAuth } from '../../redux/authSlice';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { RiLogoutCircleLine, RiStickyNoteAddFill } from "react-icons/ri";
import { MdOutlineMoreTime } from "react-icons/md";
import { FaExchangeAlt } from "react-icons/fa";
import useAxiosInstance from '../../services/useAxiosInstance';
import { CiLogout } from "react-icons/ci";
import TodoDashboard from './TodoDashboard';
import ChatWidget from '../../chatbot-component/ChatWidget';
import StickyNotes from './StickyNotes';

const Navbar = ({ toggleSidebar }) => {
    const axiosInstance = useAxiosInstance();
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const dispatch = useDispatch();
    const { roleId } = useSelector(selectAuth);
    const roleIds = useSelector((state) => state.auth.roleId);
    const communityId = useSelector((state) => state.auth.communityId);
    const userId = useSelector((state) => state.auth.userId);
    const selectedTimeZone = useSelector((state) => state.timeZone.selectedTimeZone);
    const roleIdNav = useSelector((state) => state.auth.roleIdNav);
    const [showTodo, setShowTodo] = useState(false);
    //console.log(roleIds, roleIdNav, "hfg");
    const [communityData, setCommunityData] = useState({});

    const fetchCommunityData = async () => {
        try {
            const communityResponse = await axiosInstance.get(`/api/public/community/getOne/${communityId}`);
            const communityData = communityResponse.data;
            setCommunityData(communityData);
            const contractId = communityData.contractId;
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        fetchCommunityData(communityId)
    }, [])

    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        userProfileUrl: null,
    });
    const [currentDateTime, setCurrentDateTime] = useState('');

    const toggleDropdown = () => {
        setDropdownOpen(!isDropdownOpen);
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    const handleAdminBtnClick = () => {
        dispatch(toggleAuthentication());
    };

    const handleRoleToggle = () => {
        dispatch(toggleRole());
        // Remove the immediate reload - let the component update dynamically first
        setTimeout(() => {
            window.location.reload();
            window.location.href = '/main#';
        }, 300);
    };

    // Role title mapping - now using current roleId instead of roleIdNav
    const getRoleTitle = () => {
        const roleTitles = {
            1000: "Super Admin",
            1001: "HOA Admin",
            1002: "HOA Board Member",
            1003: "Home Owner", // Changed from "Welcome User" to "User" for cleaner display
            1004: "Welcome Manager",
            1005: "Welcome Employee"
        };

        return roleTitles[roleId] || ""; // Using roleId instead of roleIdNav
    };

    // Fetch user data based on current active role
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                // Try to fetch role-specific user data first
                let response;
                try {
                    response = await axiosInstance.get(`/api/user/getById/${userId}`, {
                        params: {
                            roleId: roleId, // Use current active roleId
                            communityId: communityId
                        }
                    });
                } catch (roleSpecificError) {
                    // If role-specific API fails, fall back to basic user data
                    console.log('Role-specific fetch failed, using basic user data');
                    response = await axiosInstance.get(`/api/user/getById/${userId}`);
                }

                const { firstName, lastName, userProfileUrl } = response.data;
                setUserData({
                    firstName: firstName || '',
                    lastName: lastName || '',
                    userProfileUrl: userProfileUrl || null
                });
            } catch (error) {
                console.error('Error fetching user data:', error);
                // Set default values if API fails
                setUserData({
                    firstName: '',
                    lastName: '',
                    userProfileUrl: null,
                });
            }
        };

        if (userId && axiosInstance) {
            fetchUserData();
        }

        const updateDateTime = () => {
            const now = new Date();
            const options = {
                timeZone: selectedTimeZone,
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            };
            const formattedDateTime = now.toLocaleString('en-US', options);
            setCurrentDateTime(formattedDateTime);
        };

        updateDateTime();
        const intervalId = setInterval(updateDateTime, 60000);

        return () => clearInterval(intervalId);
    }, [userId, roleId, communityId, selectedTimeZone]); // Added roleId as dependency

    useEffect(() => {
        const closeDropdown = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('click', closeDropdown);

        return () => {
            document.removeEventListener('click', closeDropdown);
        };
    }, []);

    // Helper function to get display name
    const getDisplayName = () => {
        const firstName = userData?.firstName || '';
        const lastName = userData?.lastName || '';
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        return firstName;
    };

    return (
        <div className="sticky top-0 z-50 shadow-md">
            <div className="flex items-center justify-between bg-white px-2 sm:px-4 py-2.5">
                <div className="flex items-center">
                    <CiMenuBurger
                        className="cursor-pointer text-xl sm:text-2xl text-gray-700 md:hidden block"
                        onClick={toggleSidebar}
                    />
                    <h2 className="ml-2 text-primary text-sm sm:text-base md:text-lg font-medium truncate max-w-[150px] sm:max-w-full">
                        {getRoleTitle()} {roleId !== 1000 && <span>({communityData.name})</span>}
                    </h2>
                </div>

                <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
                    {(roleIdNav === 1001 || roleIdNav === 1002) && (
                        <div className="flex items-center">
                            <label className="relative inline-block w-[50px] sm:w-[60px] h-[26px] sm:h-[30px]">
                                <input
                                    type="checkbox"
                                    className="opacity-0 w-0 h-0 peer"
                                    checked={roleId !== 1003}
                                    onChange={handleRoleToggle}
                                />
                                <span className="absolute cursor-pointer inset-0 bg-blue-300 transition-all duration-300 rounded-full
                                    before:absolute before:content-[''] before:h-[22px] sm:before:h-[26px] before:w-[22px] sm:before:w-[26px] 
                                    before:left-[2px] before:bottom-[2px] before:bg-white before:transition-all before:duration-300
                                    peer-checked:bg-primary peer-focus:shadow-md 
                                    peer-checked:before:translate-x-[24px] sm:peer-checked:before:translate-x-[29px] before:rounded-full">
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Chat Widget - Only for role 1003 (Home Owner) */}
                    {roleId === 1003 && <ChatWidget
                        buttonText=""
                        buttonStyle={{
                            backgroundColor: '#10B981',
                            // padding: '8px 16px',
                            fontSize: '20px',
                            color: '#fff',
                            marginLeft: '10px',
                        }}
                    />}

                    {/* Todo List Button - Only for roles 1001 (HOA Admin) and 1002 (HOA Board Member) */}
                    {(roleId === 1001 || roleId === 1002) && (
                        <div className="flex justify-center items-center cursor-pointer bg-blue-400 hover:bg-blue-500 rounded-full text-white"
                            onClick={() => { setShowTodo(!showTodo) }}>
                            <div className="relative p-2 rounded-full transition-colors">
                                <RiStickyNoteAddFill className="text-xl sm:text-2xl text-white" />
                            </div>
                        </div>
                    )}

                    {/* <div className="flex justify-center items-center">
                        <Link to={'/notification'} className="relative p-1 sm:p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                            <IoIosNotificationsOutline className="text-xl sm:text-2xl text-gray-700" />
                            <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>
                        </Link>
                    </div> */}

                    <div
                        onClick={toggleDropdown}
                        ref={dropdownRef}
                        className={`flex items-center justify-between px-1 sm:px-2.5 py-1.5 sm:py-2.5 rounded-lg cursor-pointer transition-all 
                            ${isDropdownOpen ? 'shadow-md bg-gray-50' : ''} border border-transparent hover:border-gray-200`}
                    >
                        <div className="w-8 h-8 sm:w-10 sm:h-10 overflow-hidden rounded-full">
                            <img
                                src={userData.userProfileUrl || require("../../assets/profile.png")}
                                alt="Profile"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="ml-2 flex-1 hidden sm:block">
                            <h4 className="m-0 text-xs sm:text-sm font-semibold text-gray-800 truncate max-w-[100px] md:max-w-full">
                                {getDisplayName()}
                            </h4>
                            <p className="m-0 text-xs text-gray-500 hidden sm:block">
                                {currentDateTime}
                            </p>
                        </div>
                        <div className="text-gray-600 ml-1 sm:ml-2">
                            <IoIosArrowDown className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                        </div>

                        {isDropdownOpen && (
                            <div className="absolute top-full right-1.5 mt-1 bg-white shadow-lg rounded-lg overflow-hidden z-[1000] w-[175px] sm:w-[250px] border border-gray-100">
                                <div className="px-4 py-3 border-b border-gray-100 sm:flex sm:items-center hidden">
                                    <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                                        <img
                                            src={userData.userProfileUrl || require("../../assets/profile.png")}
                                            alt="Profile"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-800">
                                            {getDisplayName()}
                                        </h4>
                                        <p className="text-xs text-gray-500">{getRoleTitle()}</p>
                                    </div>
                                </div>
                                <Link to={'/time-zone'} className="flex items-center px-4 py-2.5 no-underline text-gray-700 text-sm hover:bg-gray-50 transition-colors">
                                    <MdOutlineMoreTime className="text-lg mr-2 text-gray-500" />
                                    <span>Set time zone</span>
                                </Link>
                                <Link to={'/change-password'} className="flex items-center px-4 py-2.5 no-underline text-gray-700 text-sm hover:bg-gray-50 transition-colors">
                                    <FaExchangeAlt className="text-lg mr-2 text-gray-500" />
                                    <span>Change password</span>
                                </Link>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center px-4 py-2.5 m-0 cursor-pointer text-gray-700 text-sm hover:bg-gray-50 transition-colors text-left"
                                >
                                    <CiLogout className="text-lg mr-2 text-gray-500" />
                                    <span>Logout</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Todo Dashboard - Only for roles 1001 and 1002 */}
                    {(roleId === 1001 || roleId === 1002) && showTodo && (
                        <div className='relative bg-white shadow-lg rounded-lg overflow-hidden'>
                            {/* <TodoDashboard isOpen={showTodo} setIsOpen={setShowTodo} /> */}
                            <StickyNotes isOpen={showTodo} setIsOpen={setShowTodo} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Navbar;